import * as React from "react";
import {
    GridRowsProp,
    DataGrid,
    GridCallbackDetails,
    GridColDef,
    GridRenderCellParams,

} from "@mui/x-data-grid";

import localisations from "../../localisation/index";
import { appInsights } from "../../../../app-insights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import Checkbox from "@mui/material/Checkbox";
import { QueryConsistencyChecksResponse, queryConsistencyCheck, updateConsistencyCheck } from "domains/consistency-check/services/consistency-check-service";

type ConsistencyCheckTableProps = {
    fieldCompanyId: number;
    onLastUpdatedDateTime: (lastUpdatedDate: string) => void | null;
    onError: (onError: boolean) => void | null;
    setVirtualisation?: boolean | undefined;
    setColumnMenu?: boolean | undefined;
};

function renderInvestigatedCheckbox(params: GridRenderCellParams<any, Boolean>) {
    const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCheckedValue = event.target.checked;

        await updateConsistencyCheck(params.id.toString(), {
            investigated: newCheckedValue
        }).then(_re => {
            params.api.setCellMode(params.id, 'investigated', 'edit');
            params.api.setEditCellValue({ id: params.id, field: 'investigated', value: newCheckedValue }, event);
            params.api.commitCellChange({ id: params.id, field: 'investigated' });
            params.api.setCellMode(params.id, 'investigated', 'view');
        });
    };

    return (
        <Checkbox
            data-testid={`checkbox-${params.id}`}
            inputProps={{ 'role': `checkbox-${params.id}` }}
            checked={params.value}
            onChange={handleCheckboxChange}
        />
    );
};

function GetColumns() {
    let {
        columns: { productId, sku, stockLedgerQuantity, warehouseQuantity, difference, investigated },
    } = localisations.consistencyCheckTable;

    let columnDefinitions = [
        {
            field: "productId",
            headerName: productId.headerName,
            description: productId.description,
            flex: 0.3,
            sortable: false
        },
        {
            field: "sku",
            headerName: sku.headerName,
            flex: 0.2,
            sortable: false
        },
        {
            field: "stockLedgerQuantity",
            headerName: stockLedgerQuantity.headerName,
            description: stockLedgerQuantity.description,
            flex: 0.3,
            sortable: false
        },
        {
            field: "warehouseQuantity",
            headerName: warehouseQuantity.headerName,
            description: warehouseQuantity.description,
            flex: 0.3,
            sortable: false
        },
        {
            field: "difference",
            headerName: difference.headerName,
            description: difference.description,
            flex: 0.3,
            sortable: false
        },
        {
            field: "investigated",
            headerName: investigated.headerName,
            description: investigated.description,
            renderCell: renderInvestigatedCheckbox,
            flex: 0.3,
            sortable: false
        },
    ] as GridColDef[];

    return columnDefinitions;
}

function ConsistencyCheckTable(props: ConsistencyCheckTableProps) {
    const { fieldCompanyId, onLastUpdatedDateTime, setVirtualisation, setColumnMenu, onError } = props

    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [rowCount, setRowCount] = React.useState<number | null>(0);
    const [loading, setLoading] = React.useState<boolean>(false);


    const handlePageChange = (pageNumber: number, _: GridCallbackDetails) => {
        if (page !== pageNumber) {
            setPage(pageNumber);
        }
    };

    const handlePageSizeChange = (pageSizeNumber: number, _: GridCallbackDetails) => {
        if (pageSize !== pageSizeNumber) {
            setPage(0);
            setPageSize(pageSizeNumber);
        }
    };

    React.useEffect(() => {
        let active = true;

        (async () => {
            setLoading(true);
            try {
                appInsights.trackTrace({ message: "Querying ConsistencyCheck Api", severityLevel: SeverityLevel.Information });

                let queryResponse: QueryConsistencyChecksResponse;
                queryResponse = await queryConsistencyCheck({
                    fieldCompanyId: fieldCompanyId,
                    pageToken: (page + 1).toString(),
                    itemsPerPage: pageSize,
                });
                onLastUpdatedDateTime(queryResponse.lastUpdatedDate);

                // Active flag here presumably prevents async code from executing if the page is disposed.
                if (!active) {
                    return;
                }

                if (queryResponse.items.length > 0) {
                    setRowCount(queryResponse.totalCount);
                    setRows(queryResponse.items);
                } else {
                    onLastUpdatedDateTime("unknown");
                    setRowCount(0);
                    setRows([]);
                }
            } catch (ex) {
                onError(true);
            } finally {
                setLoading(false);
            }
        })();

        // Set Active flag false if this is disposed. This is relevant if an Async call is in progress whilst page is disposed.
        return () => {
            active = false;
        }

    }, [page, pageSize, fieldCompanyId, onLastUpdatedDateTime]);

    return (
        <DataGrid
            autoHeight
            rows={rows}
            columns={GetColumns()}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[5, 10, 20]}
            loading={loading}
            rowCount={rowCount == null ? undefined : rowCount}
            paginationMode="server"
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            disableColumnFilter
            disableColumnMenu={setColumnMenu}
            disableVirtualization={setVirtualisation}
        />
    );
}

export default ConsistencyCheckTable;